import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Calendar } from "lucide-react";
import api from "@/lib/api";
import PageSeo from "@/components/PageSeo";
import { BookACallInline } from "@/components/BookACall";
import { SITE } from "@/constants/testIds";

const TYPES = [
  "Brand Film",
  "Employer Branding",
  "Campaign Production",
  "Founder Storytelling",
  "Corporate Film",
  "Content Series",
  "Brand Experience",
  "Other",
];

const BUDGETS = [
  "₹2L – ₹5L",
  "₹5L – ₹15L",
  "₹15L – ₹40L",
  "₹40L – ₹1Cr+",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  // Capture UTM params & referrer on first render so we can attribute the lead
  // even if the user navigates through other pages before submitting.
  const utm = useMemo(() => {
    if (typeof window === "undefined") return {};
    try {
      const p = new URLSearchParams(window.location.search);
      const src = (p.get("utm_source") || "").slice(0, 60).trim();
      const med = (p.get("utm_medium") || "").slice(0, 100).trim();
      const cmp = (p.get("utm_campaign") || "").slice(0, 150).trim();
      const ref = (document.referrer || "").slice(0, 500);
      // Persist for cross-page navigation (form is only on /contact, but user may click nav first)
      const stored = JSON.parse(sessionStorage.getItem("relatable_utm") || "{}");
      const merged = {
        utm_source: src || stored.utm_source || "",
        utm_medium: med || stored.utm_medium || "",
        utm_campaign: cmp || stored.utm_campaign || "",
        referrer: ref || stored.referrer || "",
      };
      if (src || med || cmp) sessionStorage.setItem("relatable_utm", JSON.stringify(merged));
      return merged;
    } catch {
      return {};
    }
  }, []);

  // Store UTM into sessionStorage on mount even if the user hasn't visited /contact yet
  useEffect(() => {
    // no-op; useMemo above already handles persistence on first render
  }, []);

  const handle = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      // Persist name/email so the Calendly booking widget can pre-fill them
      // without the visitor retyping — shared with BookACallFab / BookACallInline.
      if (typeof window !== "undefined" && (k === "name" || k === "email")) {
        try {
          window.localStorage.setItem(
            "relatable_contact_prefill",
            JSON.stringify({ name: next.name, email: next.email }),
          );
        } catch { /* storage disabled — non-fatal */ }
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const payload = {
        ...form,
        source: utm.utm_source || "contact_page",
        ...(utm.utm_medium ? { utm_medium: utm.utm_medium } : {}),
        ...(utm.utm_campaign ? { utm_campaign: utm.utm_campaign } : {}),
        ...(utm.referrer ? { referrer: utm.referrer } : {}),
      };
      await api.post("/leads", payload);
      setStatus("success");
      setForm({ name: "", email: "", company: "", project_type: "", budget: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <main data-testid={SITE.contactPage} className="bg-ink text-bone min-h-screen">
      <PageSeo
        title="Contact Relatable Studio · Start A Brand Film, Campaign or Documentary"
        description="Tell us what isn't working. We respond within 24 hours, often within 4. BHIVE Platinum, Church Street Campus, Bengaluru. nexa@relatable.studio."
        path="/contact"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": { "@id": "https://relatable.studio/#organization" }
        }}
      />
      <section className="pt-28 md:pt-32 pb-10 px-6 md:px-12 lg:px-24">
        <p className="eyebrow text-fog mb-6">◇ Contact — Bangalore · India</p>
        <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter font-medium leading-[0.92]">
          Tell us<br/>the <span className="text-relatable">problem.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-fog text-base md:text-lg leading-relaxed">
          Skip the ask for a quote. Skip the deck. Tell us what isn&rsquo;t working — recruitment, launch, founder visibility, agency execution, internal comms. We&rsquo;ll come back within 24 hours with a 30-minute discovery call.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-20">
        <div className="grid grid-cols-12 gap-8 md:gap-12 border-t border-line pt-10">
          <motion.form
            onSubmit={submit}
            data-testid={SITE.contactForm}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="col-span-12 md:col-span-8 space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="eyebrow text-fog block mb-3">01 — Your name</label>
                <input
                  data-testid={SITE.contactName}
                  required
                  value={form.name}
                  onChange={handle("name")}
                  type="text"
                  className="w-full bg-transparent border-b border-line py-3 focus:border-relatable outline-none text-bone text-lg"
                />
              </div>
              <div>
                <label className="eyebrow text-fog block mb-3">02 — Email</label>
                <input
                  data-testid={SITE.contactEmail}
                  required
                  value={form.email}
                  onChange={handle("email")}
                  type="email"
                  className="w-full bg-transparent border-b border-line py-3 focus:border-relatable outline-none text-bone text-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="eyebrow text-fog block mb-3">03 — Company / Brand</label>
                <input
                  data-testid={SITE.contactCompany}
                  value={form.company}
                  onChange={handle("company")}
                  type="text"
                  className="w-full bg-transparent border-b border-line py-3 focus:border-relatable outline-none text-bone text-lg"
                />
              </div>
              <div>
                <label className="eyebrow text-fog block mb-3">04 — Project type</label>
                <select
                  data-testid={SITE.contactType}
                  value={form.project_type}
                  onChange={handle("project_type")}
                  className="w-full bg-transparent border-b border-line py-3 focus:border-relatable outline-none text-bone text-lg appearance-none"
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%23A1A1A1' fill='none' stroke-width='1.5'/></svg>\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}
                >
                  <option value="" className="bg-ink">— select —</option>
                  {TYPES.map((t) => <option key={t} value={t} className="bg-ink">{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="eyebrow text-fog block mb-3">05 — Indicative budget</label>
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b}
                    onClick={() => setForm((f) => ({ ...f, budget: b }))}
                    data-testid={form.budget === b ? `${SITE.contactBudget}-active` : undefined}
                    className={`eyebrow px-4 py-2 border transition-colors ${
                      form.budget === b ? "border-bone bg-bone text-ink" : "border-line text-fog hover:text-bone hover:border-bone"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="eyebrow text-fog block mb-3">06 — What&rsquo;s the problem?</label>
              <textarea
                data-testid={SITE.contactMessage}
                required
                value={form.message}
                onChange={handle("message")}
                rows={6}
                placeholder="Tell us what isn't working. The more specific, the better."
                className="w-full bg-transparent border-b border-line py-3 focus:border-relatable outline-none text-bone text-lg resize-none"
              />
            </div>

            {status === "success" && (
              <p className="eyebrow text-relatable inline-flex items-center gap-2">
                <Check className="w-4 h-4" /> Brief received. We&rsquo;ll reply within 24 hours.
              </p>
            )}
            {status === "error" && <p className="eyebrow text-red-400">{error}</p>}

            <div className="pt-4">
              <button
                type="submit"
                data-testid={SITE.contactSubmit}
                disabled={status === "loading"}
                className="inline-flex items-center gap-3 px-8 py-5 bg-bone text-ink eyebrow text-sm hover:bg-relatable hover:text-bone transition-colors disabled:opacity-60"
              >
                {status === "loading" ? "Sending…" : "Send Brief"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.form>

          <aside className="col-span-12 md:col-span-4 space-y-12">
            <div>
              <p className="eyebrow text-fog mb-4">Direct</p>
              <a href="mailto:nexa@relatable.studio" className="block font-display text-2xl md:text-3xl text-bone link-line" data-testid="contact-email">nexa@relatable.studio</a>
              <a
                href="https://wa.me/919380021206?text=Hi%20Relatable%20Studio%20%E2%80%94%20I%27d%20like%20to%20discuss%20a%20project."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="mt-4 inline-flex items-center gap-2 font-display text-xl md:text-2xl text-bone link-line"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true" className="text-relatable"><path d="M20.52 3.48A11.94 11.94 0 0 0 12.02 0C5.4 0 .04 5.36.04 11.98c0 2.11.55 4.17 1.6 5.98L0 24l6.2-1.62a11.98 11.98 0 0 0 5.82 1.48h.01c6.62 0 11.98-5.36 11.98-11.98 0-3.2-1.25-6.21-3.49-8.4Zm-8.5 18.42h-.01a9.96 9.96 0 0 1-5.08-1.39l-.36-.22-3.68.96.98-3.58-.24-.37a9.94 9.94 0 0 1-1.52-5.32C2.11 6.5 6.55 2.06 12.02 2.06c2.66 0 5.16 1.04 7.04 2.92a9.9 9.9 0 0 1 2.92 7.04c0 5.47-4.45 9.88-9.96 9.88Zm5.44-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.91-2.19-.24-.57-.48-.49-.66-.5l-.56-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49 1.77.77 2.47.83 3.36.7.54-.08 1.66-.68 1.9-1.34.23-.66.23-1.22.16-1.34-.07-.12-.27-.2-.57-.35Z"/></svg>
                WhatsApp · +91 93800 21206
              </a>
            </div>
            <div>
              <p className="eyebrow text-fog mb-4">Registered Office</p>
              <p className="text-bone leading-relaxed not-italic">
                Whitefield<br/>
                Bengaluru, Karnataka
              </p>
              <p className="mt-2 text-fog text-xs md:text-sm leading-relaxed">
                Registered business address
              </p>
            </div>
            <div>
              <p className="eyebrow text-fog mb-4">Bengaluru HQ</p>
              <p className="text-bone leading-relaxed not-italic">
                BHIVE Platinum<br/>
                Church Street, Bengaluru<br/>
                Karnataka
              </p>
              <p className="mt-2 text-fog text-xs md:text-sm leading-relaxed">
                Available by appointment
              </p>
            </div>
            <div>
              <p className="eyebrow text-fog mb-4">Response Time</p>
              <p className="text-bone leading-relaxed">Within 24 hours.<br/>Often within 4.</p>
            </div>
            <div>
              <p className="eyebrow text-fog mb-4">For Press / Partnerships</p>
              <p className="text-bone leading-relaxed">Same form. Mention it in the message.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Direct booking — Calendly inline widget. Skips the form entirely  */}
      {/* for visitors who already know what they want.                    */}
      {/* ---------------------------------------------------------------- */}
      <section
        data-testid="contact-book-call-section"
        className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32 border-t border-line pt-16 md:pt-20"
      >
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-4 md:sticky md:top-28 self-start">
            <p className="eyebrow text-fog mb-4 inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.6} /> Or — book directly
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium leading-[0.95] text-bone">
              Pick a slot.<br/>
              <span className="text-relatable">Skip the brief.</span>
            </h2>
            <p className="mt-6 text-fog text-base md:text-lg leading-relaxed max-w-md">
              30 minutes on the calendar with our founders. Zero deck required — bring the problem, we&rsquo;ll bring the shape of an answer.
            </p>
            <ul className="mt-8 space-y-2 text-sm text-fog">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-relatable shrink-0 mt-0.5" strokeWidth={2}/> Confirmed instantly on your calendar</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-relatable shrink-0 mt-0.5" strokeWidth={2}/> Google Meet link auto-attached</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-relatable shrink-0 mt-0.5" strokeWidth={2}/> Reschedule / cancel with one click</li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-8">
            <BookACallInline />
          </div>
        </div>
      </section>
    </main>
  );
}
